import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // Enable sending cookies
  timeout: 30000, // 30 second timeout
  maxContentLength: 20 * 1024 * 1024, // 20MB max content length
  maxBodyLength: 20 * 1024 * 1024 // 20MB max body length for file uploads
});

// Add request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    // Try to get token from user object first (set by auth service)
    const userStr = localStorage.getItem('user');
    let token = null;

    if (userStr) {
      try {
        const user = JSON.parse(userStr);
        token = user.token;
      } catch (e) {
        console.error('Error parsing user from localStorage:', e);
      }
    }

    // Fallback to separate token key (legacy)
    if (!token) {
      token = localStorage.getItem('token');
    }

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    console.error('Request error:', error);
    return Promise.reject(error);
  }
);

// Add response interceptor to handle errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    console.error('API Error:', {
      status: error.response?.status,
      data: error.response?.data,
      message: error.message
    });
    return Promise.reject(error);
  }
);

// Auth endpoints
export const register = (userData) => api.post('/api/auth/register', userData);
export const login = (credentials) => api.post('/api/auth/login', credentials);
export const googleAuth = (credential) => api.post('/api/auth/google', { credential });
export const getCurrentUser = () => api.get('/api/auth/me');
export const forgotPassword = (email) => api.post('/api/auth/forgot-password', { email });
export const resetPassword = (token, password) => api.post('/api/auth/reset-password', { token, password });

// Public marketing form (no auth)
export const requestDemo = (data) => api.post('/api/request-demo', data);

// Job endpoints
export const getJobs = () => api.get('/api/jobs');
export const createJob = (jobData) => api.post('/api/jobs', jobData);
export const updateJob = (id, jobData) => api.put(`/api/jobs/${id}`, jobData);
export const deleteJob = (id) => api.delete(`/api/jobs/${id}`);

// Candidate endpoints
export const getCandidates = () => api.get('/api/candidates');
export const createCandidate = (candidateData) => api.post('/api/candidates', candidateData);
export const updateCandidate = (id, candidateData) => api.put(`/api/candidates/${id}`, candidateData);
export const deleteCandidate = (id) => api.delete(`/api/candidates/${id}`);

// Talent Pool endpoints (CandidateSubmission model - main candidate source)
export const getTalentPool = () => api.get('/api/talent');
export const getTalentCandidate = (id) => api.get(`/api/talent/${id}`);
export const searchCandidates = (jobDescription) => api.post('/api/candidates/search', { jobDescription });
export const testUpload = (file) => {
  console.log('Testing file upload with:', file.name);
  
  // Create a clean FormData object
  const testFormData = new FormData();
  testFormData.append('file', file, file.name);
  
  // Get auth token
  const token = localStorage.getItem('token');
  
  // Create headers with authorization if token exists
  const headers = {};
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  
  // No Content-Type - browser will set it with boundary for multipart/form-data
  
  return fetch(`${API_URL}/api/candidates/test-upload`, {
    method: 'POST',
    body: testFormData,
    headers,
    credentials: 'include'
  })
  .then(response => {
    console.log(`Test upload response status: ${response.status}`);
    if (!response.ok) {
      return response.json().then(err => {
        console.error('Test upload error response:', err);
        throw new Error(err.error || err.message || `Server error: ${response.status}`);
      });
    }
    return response.json();
  })
  .catch(error => {
    console.error('Test upload error:', error);
    throw error;
  });
};

export const screenResume = (formData) => {
  console.log('API screenResume called with formData');
  
  // Force formData entries to be logged
  for (let pair of formData.entries()) {
    console.log(`FormData entry - ${pair[0]}: ${pair[1] instanceof File ? 'File: ' + pair[1].name : pair[1]}`);
  }
  
  // Get authentication token
  const token = localStorage.getItem('token');
  
  // Create headers with authorization if token exists
  const headers = {};
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  
  // Use fetch instead of axios for multipart/form-data
  return fetch(`${API_URL}/api/candidates/screen-resume`, {
    method: 'POST',
    body: formData,
    headers, // Don't set Content-Type here - browser will set it with boundary for multipart/form-data
    credentials: 'include'
  }).then(response => {
    console.log(`Resume screening response status: ${response.status}`);
    
    if (!response.ok) {
      return response.json()
        .then(err => {
          console.error('Resume screening error:', err);
          throw new Error(err.error || `Server error: ${response.status}`);
        })
        .catch(jsonErr => {
          // Handle case where response is not JSON
          throw new Error(`Server error: ${response.status} - ${jsonErr.message || 'Unknown error'}`);
        });
    }
    
    return response.json();
  }).catch(error => {
    console.error('Resume screening fetch error:', error);
    throw error;
  });
};

// Outreach endpoints
export const generateMessages = (data) => api.post('/api/outreach/generate', data);
export const analyzeMatch = (data) => api.post('/api/outreach/analyze-match', data);
export const generatePersonalizedOutreach = (data) => api.post('/api/outreach/personalized-outreach', data)
  .then(response => {
    // Ensure messages have the expected structure
    if (response.data && response.data.messages) {
      response.data.messages = {
        linkedin: Array.isArray(response.data.messages.linkedin) ? response.data.messages.linkedin : [],
        email: Array.isArray(response.data.messages.email) ? response.data.messages.email : []
      };
    } else {
      response.data.messages = { linkedin: [], email: [] };
    }
    return response;
  });
export const sendMessage = (data) => api.post('/api/outreach/send', data);
export const sendEmail = (data) => api.post('/api/outreach/send-email', data);
export const getEmailStatus = () => api.get('/api/outreach/email-status');

// Smart Outreach endpoints (AI-powered with match analysis)
export const generateSmartOutreach = (data) => api.post('/api/outreach/smart-outreach', data)
  .then(response => {
    // Normalize message format for frontend consumption
    if (response.data && response.data.messages) {
      const messages = response.data.messages;
      // Convert structured messages to display format
      if (messages.linkedin) {
        messages.linkedin = messages.linkedin.map(msg =>
          typeof msg === 'object' ? msg : { message: msg, channel: 'linkedin' }
        );
      }
      if (messages.email) {
        messages.email = messages.email.map(msg =>
          typeof msg === 'object' ? msg : { message: msg, channel: 'email' }
        );
      }
    }
    return response;
  });
export const generateQuickOutreach = (data) => api.post('/api/outreach/quick', data);

// Job Scraping endpoints
export const searchJobs = (data) => api.post('/api/job-scraping/search', data);
export const saveScrapedJobs = (data) => api.post('/api/job-scraping/save', data);
export const getJobsByCandidateId = (candidateId) => api.get(`/api/job-scraping/jobs-by-candidate/${candidateId}`);
export const searchMatchingJobs = (data, isMultipart = false) => {
  if (isMultipart) {
    // Get authentication token - use mock token for development
    const token = localStorage.getItem('token');
    if (!token && process.env.NODE_ENV === 'development') {
      console.log('Development environment detected, using mock token');
      localStorage.setItem('token', 'mock-token-for-development');
    }
    
    // For resume uploads (multipart/form-data)
    console.log('Sending multipart resume data to job matching endpoint');
    
    // Log the FormData contents for debugging
    if (data instanceof FormData) {
      for (let pair of data.entries()) {
        console.log(`Job matching FormData - ${pair[0]}: ${pair[1] instanceof File ? 'File: ' + pair[1].name : pair[1]}`);
      }
    }
    
    return fetch(`${API_URL}/api/job-scraping/match-resume`, {
      method: 'POST',
      body: data,
      credentials: 'include',
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token') || 'mock-token-for-development'}`
        // Don't set Content-Type - browser will set it with boundary for multipart/form-data
      }
    }).then(response => {
      console.log(`Job matching response status: ${response.status}`);
      
      if (!response.ok) {
        return response.json()
          .then(err => {
            console.error('Job matching error:', err);
            throw new Error(err.error || `Server error: ${response.status}`);
          })
          .catch(jsonErr => {
            // Handle case where response is not JSON
            throw new Error(`Server error: ${response.status} - ${jsonErr.message || 'Unknown error'}`);
          });
      }
      return response.json();
    }).then(data => {
      console.log('Job matching successful, found', data.matchedJobs?.length || 0, 'jobs');
      return { data };
    }).catch(error => {
      console.error('Job matching fetch error:', error);
      throw error;
    });
  }
  
  // Try search endpoint first if there's a query
  if (data.query) {
    console.log('Using search endpoint for job matching with query:', data.query);
    
    // Ensure auth token is set (add mock token for development if not present)
    if (!localStorage.getItem('token') && process.env.NODE_ENV === 'development') {
      localStorage.setItem('token', 'mock-token-for-development');
      console.log('Added mock token for development environment');
    }
    
    return searchJobs({
      query: data.query,
      location: data.location || 'Remote'
    });
  }
  
  // Fall back to candidate-based searches
  return api.post('/api/job-scraping/match-candidate', data);
};

// Autopilot endpoints
export const getAutopilotStatus = () => api.get('/api/autopilot/status');
export const getAutopilotJobs = () => api.get('/api/autopilot/jobs');
export const scanCandidatesForJob = (jobId, options = {}) =>
  api.post(`/api/autopilot/scan/${jobId}`, options);
export const generateAutopilotOutreach = (data) =>
  api.post('/api/autopilot/generate-outreach', data);
export const sendBulkEmails = (data) =>
  api.post('/api/autopilot/send-bulk', data);
export const previewAutopilotEmail = (data) =>
  api.post('/api/autopilot/preview-email', data);
export const getAutopilotCandidates = (params = {}) =>
  api.get('/api/autopilot/candidates', { params });
export const getAutopilotLogs = (limit = 100) =>
  api.get('/api/autopilot/logs', { params: { limit } });

// Analytics endpoints
export const getAnalyticsOverview = () => api.get('/api/analytics/overview');
export const getAnalyticsOutreach = (days = 30) =>
  api.get('/api/analytics/outreach', { params: { days } });
export const getAnalyticsAutopilot = (days = 30) =>
  api.get('/api/analytics/autopilot', { params: { days } });
export const getAnalyticsJobs = () => api.get('/api/analytics/jobs');
export const getAnalyticsFunnel = (days = 30, jobId = null) =>
  api.get('/api/analytics/funnel', { params: { days, ...(jobId && { jobId }) } });

// Calendar Integration endpoints
// Add timestamp to prevent caching
export const getIntegrationsStatus = () =>
  api.get('/api/integrations/status', { params: { _t: Date.now() } });

// Calendly OAuth endpoints (cache-busted to prevent cross-user state leakage)
export const getCalendlyAuthUrl = (returnUrl = '/integrations') =>
  api.get('/api/integrations/calendly/auth', {
    params: { returnUrl, _t: Date.now() }
  });
export const disconnectCalendly = () => api.post('/api/integrations/calendly/disconnect');
export const getCalendlyStatus = () => api.get('/api/integrations/calendly/status');
export const getCalendlyEventTypes = () => api.get('/api/integrations/calendly/event-types');

// Calendly event operations
export const getCalendlyEvents = (status = 'active', count = 20) =>
  api.get('/api/integrations/calendly/events', { params: { status, count } });
export const createCalendlyMeeting = (data) =>
  api.post('/api/integrations/calendly/create-meeting', data);

// Google Calendar OAuth endpoints (cache-busted to prevent cross-user state leakage)
export const getGoogleCalendarAuthUrl = (returnUrl = '/integrations') =>
  api.get('/api/integrations/google/auth', {
    params: { returnUrl, _t: Date.now() }
  });
export const disconnectGoogleCalendar = () => api.post('/api/integrations/google/disconnect');
export const getGoogleCalendarStatus = () => api.get('/api/integrations/google/status');
export const getGoogleCalendars = () => api.get('/api/integrations/google/calendars');
export const setGoogleCalendar = (calendarId, calendarName) =>
  api.put('/api/integrations/google/calendar', { calendarId, calendarName });

// Google Calendar event operations (using OAuth)
export const getGoogleCalendarEvents = (limit = 10, query) =>
  api.get('/api/integrations/google/events', { params: { limit, query } });
export const createGoogleCalendarEvent = (eventData) =>
  api.post('/api/integrations/google/events', eventData);
export const deleteGoogleCalendarEvent = (eventId, calendarId) =>
  api.delete(`/api/integrations/google/events/${eventId}`, { params: { calendarId } });

// Schedule interview (convenience method)
export const scheduleInterview = (candidateId, interviewDetails) =>
  api.post('/api/integrations/google/events', { candidateId, ...interviewDetails });

// Job Recommendations endpoints
export const getJobRecommendations = (candidateId) =>
  api.post('/api/recommendations/jobs-for-candidate', { candidateId });

// Mock data endpoints (development only)
export const generateMockData = () => api.post('/api/mock/generate');
export const clearMockData = () => api.delete('/api/mock/clear');

// Pipeline endpoints (Candidate Pipeline Management)
export const getPipeline = (params) => api.get('/api/pipeline', { params });
export const moveCandidateInPipeline = (candidateId, newStage, note) =>
  api.post('/api/pipeline/move', { candidateId, newStage, note });
export const getPipelineHistory = (candidateId) => api.get(`/api/pipeline/${candidateId}/history`);
export const addPipelineNote = (candidateId, content) =>
  api.post(`/api/pipeline/${candidateId}/note`, { content });
export const getPipelineNotes = (candidateId) => api.get(`/api/pipeline/${candidateId}/notes`);
export const getPipelineStats = () => api.get('/api/pipeline/stats');

// Candidate Jobs endpoints (Multi-Job Mapping)
export const assignJobsToCandidate = (candidateId, jobIds) =>
  api.post(`/api/candidate/${candidateId}/jobs`, { jobIds });
export const getCandidateJobs = (candidateId, params = {}) =>
  api.get(`/api/candidate/${candidateId}/jobs`, { params });
export const removeCandidateJob = (candidateId, jobId) =>
  api.delete(`/api/candidate/${candidateId}/jobs/${jobId}`);
export const updateCandidateJobStatus = (candidateId, jobId, status) =>
  api.put(`/api/candidate/${candidateId}/jobs/${jobId}`, { status });
export const recalculateCandidateJobScore = (candidateId, jobId) =>
  api.post(`/api/candidate/${candidateId}/jobs/${jobId}/recalculate`);
export const recalculateAllCandidateJobScores = (candidateId) =>
  api.post(`/api/candidate/${candidateId}/jobs/recalculate-all`);

// Candidate agent — match a candidate to internal (on-platform) roles + apply in
export const getCandidateMatches = (candidateId) => api.get(`/api/agent/matches/${candidateId}`);
export const applyToInternalJob = (candidateId, jobId) => api.post('/api/agent/apply', { candidateId, jobId });
export const getCandidateApplications = (candidateId) => api.get(`/api/agent/applications/${candidateId}`);

// Double opt-in warm intros + recruiter-side matching
export const requestIntro = (candidateId, jobId, message) => api.post('/api/pool/intros', { candidateId, jobId, message });
export const getMyIntros = () => api.get('/api/pool/intros');
export const getJobCandidates = (jobId) => api.get(`/api/pool/job/${jobId}/candidates`);
export const getPoolRoles = () => api.get('/api/pool/roles');

// Free ATS job-board ingestion + real-form autofill (Greenhouse/Lever/Arbeitnow)
export const ingestBoardJobs = (ats, company) => api.post('/api/jobboard/ingest', { ats, company });
export const aggregateJobs = () => api.post('/api/jobboard/aggregate', {});
export const getApplicationForm = (url, candidateId) =>
  api.get('/api/jobboard/form', { params: { url, candidateId } });

// Recruiter Pool — intent-scored candidate search (two-sided model)
export const poolSearch = (filters = {}) => api.post('/api/pool/search', filters);
export const poolStats = () => api.get('/api/pool/stats');
export const updateCandidateIntent = (candidateId, data) =>
  api.patch(`/api/pool/candidates/${candidateId}/intent`, data);
export const revealContact = (candidateId) => api.post(`/api/pool/candidates/${candidateId}/reveal`);

// Candidate self-signup into the pool (public, no auth)
export const joinPool = (data) => api.post('/api/join', data);

// Saved Filters / Smart Match Alerts endpoints
export const getSavedFilters = () => api.get('/api/filters');
export const getSavedFilter = (id) => api.get(`/api/filters/${id}`);
export const saveFilter = (filterData) => api.post('/api/filters/save', filterData);
export const updateSavedFilter = (id, filterData) => api.put(`/api/filters/${id}`, filterData);
export const deleteSavedFilter = (id) => api.delete(`/api/filters/${id}`);
export const getFilterMatches = (id) => api.get(`/api/filters/${id}/matches`);
export const getRecentAlerts = (limit = 20) =>
  api.get('/api/filters/alerts/recent', { params: { limit } });
export const checkCandidateAgainstFilters = (candidateId) =>
  api.post('/api/filters/check-candidate', { candidateId });

// Collaboration endpoints (Shared Access System)
export const shareCandidate = (candidateId, recruiterEmail, message) =>
  api.post('/api/collab/share', { candidateId, recruiterEmail, message });
export const unshareCandidate = (candidateId, recruiterId) =>
  api.post('/api/collab/unshare', { candidateId, recruiterId });
export const getSharedWithMe = (params) => api.get('/api/collab/shared', { params });
export const getSharedByMe = (params) => api.get('/api/collab/shared-by-me', { params });
export const searchRecruiters = (search) =>
  api.get('/api/collab/recruiters', { params: { search } });

// Comments endpoints (Shared discussions)
export const getCandidateComments = (candidateId) => api.get(`/api/comments/${candidateId}`);
export const addCandidateComment = (candidateId, content) =>
  api.post(`/api/comments/${candidateId}`, { content });
export const deleteCandidateComment = (candidateId, commentId) =>
  api.delete(`/api/comments/${candidateId}/${commentId}`);

// Feedback endpoints (AI Interview Feedback Summarizer)
export const summarizeFeedback = (data) => api.post('/api/feedback/summarize', data);
export const summarizeFeedbackPreview = (data) => api.post('/api/feedback/summarize-only', data);
export const getFeedbackHistory = (candidateId) => api.get(`/api/feedback/${candidateId}`);
export const getFeedbackAnalytics = (candidateId) => api.get(`/api/feedback/analytics/${candidateId}`);
export const deleteFeedback = (candidateId, feedbackId) => api.delete(`/api/feedback/${candidateId}/${feedbackId}`);

// Meeting Notetaker endpoints (transcript -> structured notes via Haiku)
export const summarizeMeeting = (data) => api.post('/api/notetaker/summarize', data);

// Notifications endpoints
export const getNotifications = (params) => api.get('/api/notifications', { params });
export const getUnreadCount = () => api.get('/api/notifications/unread-count');
export const markNotificationRead = (id) => api.put(`/api/notifications/${id}/read`);
export const markAllNotificationsRead = () => api.put('/api/notifications/read-all');
export const deleteNotification = (id) => api.delete(`/api/notifications/${id}`);
export const deleteAllNotifications = () => api.delete('/api/notifications');

// Proof-of-Skill endpoints (Feature 1 — verified work samples)
export const getProofScore = (id) => api.get(`/api/proof/scores/${id}`);
export const getProofScoreAudit = (id) => api.get(`/api/proof/scores/${id}/audit.json`);
export const getWorkSample = (id) => api.get(`/api/proof/work-samples/${id}`);
export const createWorkSample = (data) => api.post('/api/proof/work-samples', data);
export const assignWorkSample = (workSampleId, candidateId) =>
  api.post(`/api/proof/work-samples/${workSampleId}/assign`, { candidate_id: candidateId });
export const getProofSubmission = (id) => api.get(`/api/proof/submissions/${id}`);
export const submitProofResponse = (id, payload) =>
  api.post(`/api/proof/submissions/${id}/submit`, payload);
export const scoreProofSubmission = (id) => api.post(`/api/proof/submissions/${id}/score`);

// Integrity signals (Feature 2 pillar 1 — hash-chained, log-only)
export const postIntegrityEvents = (submissionId, events) =>
  api.post('/api/integrity/events', { submission_id: submissionId, events });
export const getIntegrityDigest = (submissionId) =>
  api.get(`/api/integrity/submissions/${submissionId}/digest`);

// Active face-liveness (Feature 2 pillar 2 — opt-in, consent-gated, review-only).
// Backend issues a randomized, HMAC-signed challenge so a clip can't be pre-recorded;
// the client returns only DERIVED signals (per-frame EAR/yaw/jaw-open, pass/fail).
// Endpoints 404 unless backend ENABLE_LIVENESS=true.
export const getLivenessChallenge = (submissionId) =>
  api.post('/api/liveness/challenge', { submission_id: submissionId });
export const verifyLiveness = (payload) =>
  api.post('/api/liveness/verify', payload);

// Candidate Auto-Apply endpoints (jobright.ai-style auto-apply queue + agent settings)
export const getAutoApplySettings = () => api.get('/api/autoapply/settings');
export const updateAutoApplySettings = (data) => api.put('/api/autoapply/settings', data);
export const getAutoApplyQueue = () => api.get('/api/autoapply/queue');
export const addAutoApplyJobs = (urls) => api.post('/api/autoapply/queue', { urls });
export const runAutoApply = (jobId) =>
  api.post('/api/autoapply/run', jobId ? { jobId } : {}, { timeout: 120000 });
export const getAutoApplyJob = (id) => api.get(`/api/autoapply/${id}`);
export const skipAutoApplyJob = (id) => api.post(`/api/autoapply/${id}/skip`);

export default api;